import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getAllJSON, sendJSON } from "../api.js"

// Subskrypcje popytu (backorder): lista + subskrybuj + wyłącz/włącz.
// "Usuń" = DELETE (miękka dezaktywacja po stronie API); "Włącz" = ponowny POST
// tych samych parametrów (API reaktywuje istniejącą subskrypcję — idempotentne).
// `organization` jest wysyłana JAWNIE: użytkownik w wielu org (i personel) wybiera
// ją z selecta — bez tego API odpowiada 400 (auto tylko przy dokładnie jednej org).
export default class extends Controller {
  static targets = ["rows", "status", "subcatSelect", "voivSelect", "orgWrap", "orgSelect", "formError"]
  static values = {
    url: String,               // /api/backorder-subscriptions/
    subcategoriesUrl: String,  // /api/subcategories/
    voivodeshipsUrl: String,   // /api/voivodeships/
    organizationsUrl: String,  // /api/organizations/
  }

  async connect() {
    this.subcatNames = new Map()
    this.voivNames = new Map()
    await this.#loadDictionaries()
    await this.load()
  }

  async load() {
    let subscriptions
    try {
      subscriptions = await getAllJSON(this.urlValue)
    } catch {
      this.#status("Nie udało się załadować subskrypcji. Odśwież stronę.")
      return
    }
    this.rowsTarget.textContent = ""
    if (!subscriptions.length) {
      this.#status("Brak subskrypcji. Dodaj pierwszą powyżej.")
      return
    }
    this.#status(null)
    const template = document.getElementById("backorder-row-template")
    for (const sub of subscriptions) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-field="subcat"]').textContent =
        this.subcatNames.get(sub.subcategory) ?? `#${sub.subcategory}`
      row.querySelector('[data-field="voiv"]').textContent =
        sub.voivodeship ? (this.voivNames.get(sub.voivodeship) ?? `#${sub.voivodeship}`) : "Dowolne"

      const badge = row.querySelector('[data-field="badge"]')
      badge.className = `ls-badge ${sub.is_active ? "ls-badge--sent" : "ls-badge--new"}`
      badge.textContent = sub.is_active ? "aktywna" : "wyłączona"

      row.querySelector('[data-field="count"]').textContent = sub.notify_count
      row.querySelector('[data-field="last"]').textContent =
        sub.last_notified_at ? formatDate(sub.last_notified_at) : "—"

      const toggle = row.querySelector('[data-field="toggle"]')
      toggle.textContent = sub.is_active ? "Wyłącz" : "Włącz"
      toggle.setAttribute("data-backorder-id-param", sub.id)
      toggle.setAttribute("data-backorder-active-param", sub.is_active)
      toggle.setAttribute("data-backorder-subcategory-param", sub.subcategory)
      toggle.setAttribute("data-backorder-voivodeship-param", sub.voivodeship ?? "")
      toggle.setAttribute("data-backorder-organization-param", sub.organization)
      this.rowsTarget.appendChild(row)
    }
  }

  async subscribe() {
    const subcategory = this.subcatSelectTarget.value
    if (!subcategory) {
      this.#formError("Wybierz podkategorię.")
      return
    }
    const body = { subcategory: Number(subcategory) }
    const voivodeship = this.voivSelectTarget.value
    if (voivodeship) body.voivodeship = Number(voivodeship)
    const organization = this.#chosenOrganization()
    if (organization) body.organization = organization
    const res = await sendJSON("POST", this.urlValue, body)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się dodać subskrypcji."))
      return
    }
    this.#formError(null)
    await this.load()
  }

  async toggleActive(event) {
    const { id, active, subcategory, voivodeship, organization } = event.params
    let res
    if (active) {
      res = await sendJSON("DELETE", `${this.urlValue}${id}/`)
    } else {
      // Reaktywacja = POST tych samych parametrów, z JAWNĄ organizacją z wiersza
      // (użytkownik w wielu org / personel — API nie zgadnie organizacji).
      const body = { subcategory: Number(subcategory), organization: Number(organization) }
      if (voivodeship) body.voivodeship = Number(voivodeship)
      res = await sendJSON("POST", this.urlValue, body)
    }
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się zmienić subskrypcji."))
      return
    }
    await this.load()
  }

  // --- prywatne ---

  async #loadDictionaries() {
    try {
      const [subcategories, voivodeships, organizations] = await Promise.all([
        getAllJSON(this.subcategoriesUrlValue),
        getAllJSON(this.voivodeshipsUrlValue),
        this.organizationsUrlValue ? getAllJSON(this.organizationsUrlValue) : Promise.resolve([]),
      ])
      for (const sub of subcategories) {
        this.subcatNames.set(sub.id, sub.name)
        if (this.hasSubcatSelectTarget) {
          const option = document.createElement("option")
          option.value = sub.id
          option.textContent = sub.name
          this.subcatSelectTarget.appendChild(option)
        }
      }
      for (const voivodeship of voivodeships) {
        this.voivNames.set(voivodeship.id, voivodeship.name)
        if (this.hasVoivSelectTarget) {
          const option = document.createElement("option")
          option.value = voivodeship.id
          option.textContent = voivodeship.name
          this.voivSelectTarget.appendChild(option)
        }
      }
      this.organizations = organizations
      if (this.hasOrgSelectTarget && organizations.length > 1) {
        for (const org of organizations) {
          const option = document.createElement("option")
          option.value = org.id
          option.textContent = org.name
          this.orgSelectTarget.appendChild(option)
        }
        if (this.hasOrgWrapTarget) this.orgWrapTarget.hidden = false
      }
    } catch {
      // słowniki niedostępne — formularz zostaje pusty, lista pokaże #id
    }
  }

  #chosenOrganization() {
    if (this.hasOrgSelectTarget && !this.orgSelectTarget.closest("[hidden]") && this.orgSelectTarget.value) {
      return Number(this.orgSelectTarget.value)
    }
    // Jedna org: wyślij ją jawnie (spójne z auto po stronie API; personel z 1 org też zadziała).
    if (this.organizations?.length === 1) return Number(this.organizations[0].id)
    return null
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return
    this.formErrorTarget.hidden = message === null
    if (message !== null) this.formErrorTarget.textContent = message
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
